import {ReactElement, useState} from "react";
import {Field, Input, Label, Message} from "@zendeskgarden/react-forms";
import {useChartOfAccounts} from "../../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {CustomZDDropdown} from "../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {
    useCreateRenovationCostCategoryMutation,
    useUpdateRenovationCostCategoryMutation
} from "../../../../__generated__/generated_types";
import {Modal, Header, Body, Footer, Close, FooterItem} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {toast} from "react-toastify";
import * as css from "./styles/RenovationsCostCategoryForm.module.scss";

export interface RenovationCostCategoryFormProps {
    budgetYear: number;
    initialData?: {
        id: string;
        name: string;
        accountId: string;
    }
    onSave: () => void;
}

export function RenovationCostCategoryForm(props: RenovationCostCategoryFormProps): ReactElement {
    const {accounts} = useChartOfAccounts();
    const [name, setName] = useState<string>(props.initialData?.name ?? "");
    const [nameTouched, setNameTouched] = useState<boolean>(false);
    const [accountId, setAccountId] = useState<string>(props.initialData?.accountId ?? "");
    const [accountIdTouched, setAccountIdTouched] = useState<boolean>(false);

    const [createCategory,] = useCreateRenovationCostCategoryMutation({
        notifyOnNetworkStatusChange: true
    });
    const [updateCategory,] = useUpdateRenovationCostCategoryMutation({
        notifyOnNetworkStatusChange: true
    });

    function saveCategory(): void {
        let promise: ReturnType<typeof updateCategory> | ReturnType<typeof createCategory>;
        if(props.initialData) {
            promise = updateCategory({
                variables: {
                    id: props.initialData?.id ?? "",
                    name: name,
                    accountId: accountId,
                    budgetYear: props.budgetYear
                }
            });
        } else {
            promise = createCategory({
                variables: {
                    name: name,
                    accountId: accountId,
                    budgetYear: props.budgetYear
                }
            });
        }
        promise.then(res => {
            if(res.data) {
                if(props.initialData) {
                    toast.success("Category updated successfully.");
                } else {
                    toast.success("Category created successfully.");
                }
                props.onSave();
            }
        }).catch(() => {
            if(props.initialData) {
                toast.error("Error updating category.");
            } else {
                toast.error("Error creating category.");
            }
        });
    }

    return <Modal style={{overflow: "visible"}} onClose={props.onSave}>
        <Header>{props.initialData ? "Update" : "Create"} Cost Category</Header>
        <Body>
        <form className={css.form} onSubmit={saveCategory}>
            <Field className={css.field}>
                <Label>Name</Label>
                <Input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={evt => {
                        setName(evt.target.value);
                        setNameTouched(true);
                    }}
                    validation={nameTouched && !name ? "error" : undefined}
                />
                {nameTouched && !name &&
                    <Message validation="error">Name is Required.</Message>
                }
            </Field>
            <div className={css.field}>
                <CustomZDDropdown
                    initialSelectedItems={[accountId]}
                    applySelectedItems={items => {
                        if(Array.isArray(items) && items.length > 0) {
                            setAccountId(items[0]);
                            setAccountIdTouched(true);
                        } else if(typeof items === "string") {
                            setAccountId(items);
                            setAccountIdTouched(true);
                        }
                    }}
                    openDropdownPlaceholder={"Select Account"}
                    closedDropdownPlaceholder={undefined}
                    options={accounts.map(a => ({
                        label: `${a.accountNumber} - ${a.name}`,
                        value: a.id
                    }))}
                    isError={accountIdTouched && !accountId}
                    errorMessage={"Account is Required."}
                    label="Account"
                />
            </div>
        </form>
        </Body>
        <Footer>
            <FooterItem>
                <Button isBasic onClick={props.onSave}>Cancel</Button>
            </FooterItem>
            <FooterItem>
                <Button isPrimary disabled={!name || !accountId} onClick={saveCategory}>Save</Button>
            </FooterItem>
        </Footer>
        <Close aria-label="Close modal" />
    </Modal>;
}